// The top bar's bell: unread CEISA email responses, a list of the latest with download pills per
// attached document. Same hook and behaviour as the old Mantine bell
// (src/components/notifications/NotificationBell.tsx); only the UI is rebuilt on Lumen parts.
import {
  AlertTriangleIcon,
  BellIcon,
  CheckCheckIcon,
  CheckIcon,
  CircleCheckIcon,
  ClockIcon,
  DownloadIcon,
  FileTextIcon,
  Loader2Icon,
  MailIcon,
  ReceiptIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import useEmailResponseNotifications, {
  type EmailResponseAttachment,
  type EmailResponseNotificationItem,
  parseResponseTime,
} from '@hooks/notifications/use-email-response-notifications';
import { Button } from '@/components/lumen/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/lumen/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/lumen/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import {
  capCount,
  formatAjuShortRef,
  formatElapsed,
  formatNomorAju,
  formatResponDateTime,
  formatWaktuRespon,
  type ResponseTone,
  responseTone,
} from './notification-format';
import { TopbarIconButton } from './TopbarIconButton';

/** The timer only shows minutes, so it only needs to move once a minute. */
const ELAPSED_TICK_MS = 30 * 1000;
/** How long the finished/failed state stays on a download pill before it resets. */
const DOWNLOAD_FEEDBACK_MS = { done: 2000, error: 2800 } as const;

type DownloadState = 'idle' | 'loading' | 'done' | 'error';

const TONE: Record<ResponseTone, { className: string; Icon: typeof MailIcon }> = {
  err: { className: 'bg-err/12 text-err', Icon: AlertTriangleIcon },
  ok: { className: 'bg-ok/12 text-ok', Icon: CircleCheckIcon },
  warn: { className: 'bg-warn/12 text-warn', Icon: ReceiptIcon },
  info: { className: 'bg-label/12 text-label', Icon: MailIcon },
};

/**
 * One download pill. The response is what the user came for, the declaration draft is quieter.
 * The pill reports its own download (spinner, then check or warning) because a PDF saved straight
 * to the downloads folder gives no sign at the click.
 */
function AttachmentButton({
  attachment,
  onClick,
}: { attachment: EmailResponseAttachment; onClick: () => Promise<boolean> }) {
  const { t } = useLanguage();
  const [state, setState] = useState<DownloadState>('idle');
  const resetTimer = useRef<number | null>(null);
  const mounted = useRef(true);

  // The reset is on a timer, and the popover can close (unmounting the row) before it fires.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    };
  }, []);

  const isDeclaration = attachment.kind === 'dokumenPabean';

  const handleClick = async () => {
    // A second click while the first request is open would download the same file twice.
    if (state === 'loading') return;
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);

    setState('loading');
    const ok = await onClick();
    if (!mounted.current) return;

    const outcome: DownloadState = ok ? 'done' : 'error';
    setState(outcome);
    resetTimer.current = window.setTimeout(() => {
      if (mounted.current) setState('idle');
    }, DOWNLOAD_FEEDBACK_MS[outcome]);
  };

  const isSignalling = state === 'done' || state === 'error';
  const tooltipLabel =
    state === 'loading'
      ? `${t('notifications.bell.downloading')} ${attachment.label}`
      : state === 'done'
        ? `${attachment.label} ${t('notifications.bell.downloaded')}`
        : state === 'error'
          ? `${t('notifications.bell.downloadFailed')} ${attachment.label}`
          : `${t('notifications.bell.download')} ${attachment.label}`;

  const Icon =
    state === 'loading'
      ? Loader2Icon
      : state === 'done'
        ? CheckIcon
        : state === 'error'
          ? AlertTriangleIcon
          : isDeclaration
            ? FileTextIcon
            : DownloadIcon;

  return (
    // Finished and failed hold the tooltip open so the outcome is read where the user clicked.
    <Tooltip open={isSignalling || undefined}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          aria-busy={state === 'loading'}
          aria-label={tooltipLabel}
          className={cn(
            'inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-label/40',
            isDeclaration ? 'font-medium text-fg-2' : 'font-semibold text-fg',
            state === 'done'
              ? 'border-ok bg-ok/12 text-ok-ink'
              : state === 'error'
                ? 'border-err bg-err/10 text-err-ink'
                : 'border-line-control hover:border-label hover:bg-surface-2 hover:text-fg',
            state === 'loading' && 'cursor-progress'
          )}
        >
          <Icon className={cn('size-3', state === 'loading' && 'animate-spin')} aria-hidden />
          {attachment.label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
}

function NotificationRow({
  notification,
  onOpen,
  onDownload,
}: {
  notification: EmailResponseNotificationItem;
  onOpen: (notification: EmailResponseNotificationItem) => void;
  onDownload: (notification: EmailResponseNotificationItem, attachmentIndex: number) => Promise<boolean>;
}) {
  const { t } = useLanguage();
  const tone = TONE[responseTone(notification)];
  const isUnread = !notification.isRead;
  const respon = (notification.uraianSingkatRespon || notification.kodeRespon || 'RESPON').toUpperCase();
  const shortRef = formatAjuShortRef(notification.nomorAju);

  return (
    <li className="rounded-control px-2 py-2 transition-colors hover:bg-surface-2">
      <button
        type="button"
        onClick={() => onOpen(notification)}
        className="flex w-full items-start gap-2.5 rounded-tag text-left outline-none focus-visible:ring-[3px] focus-visible:ring-label/40"
      >
        <span className={cn('mt-px flex size-6.5 shrink-0 items-center justify-center rounded-full', tone.className)}>
          <tone.Icon className="size-3.5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-2">
            {/* Response type, then whose shipment, then which one: the list is scanned down this column. */}
            <span className="line-clamp-2 flex-1 text-13 text-fg leading-[1.45]">
              <span className="font-semibold">{respon}</span>
              <span className="text-fg-muted"> - </span>
              <span className="font-semibold">{notification.namaPerusahaan || '-'}</span>
              {shortRef && <span className="font-mono text-fg-muted text-xs"> ({shortRef})</span>}
              {notification.isUrgent && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 whitespace-nowrap rounded-tag bg-err/12 px-1 align-middle font-bold text-[10px] text-err-ink uppercase tracking-wide">
                  <AlertTriangleIcon className="size-2.5" aria-hidden />
                  {t('notifications.bell.urgent')}
                </span>
              )}
            </span>
            {/* Unread is a single dot. */}
            <span
              aria-hidden
              className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', isUnread ? 'bg-label' : 'bg-transparent')}
            />
          </span>
          {/* The exact time the response landed; the full AJU is one hover away. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="mt-0.5 block truncate text-fg-muted text-xs tabular-nums">
                {formatResponDateTime(parseResponseTime(notification), notification.waktuRespon)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="font-mono">
              {formatNomorAju(notification.nomorAju) || '-'}
            </TooltipContent>
          </Tooltip>
        </span>
      </button>

      {notification.attachments.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5 pl-9">
          {notification.attachments.map((attachment) => (
            <AttachmentButton
              key={attachment.index}
              attachment={attachment}
              onClick={() => onDownload(notification, attachment.index)}
            />
          ))}
        </div>
      )}
    </li>
  );
}

export function NotificationBell() {
  const { t } = useLanguage();
  const [opened, setOpened] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const {
    items,
    unreadCount,
    hasUnread,
    lastResponseAt,
    isLoading,
    isError,
    refetch,
    markRead,
    markAllRead,
    isMarkingAllRead,
    downloadAttachment,
  } = useEmailResponseNotifications();

  const visibleItems = useMemo(
    () => (filter === 'unread' ? items.filter((item) => !item.isRead) : items),
    [items, filter]
  );

  // The elapsed reading changes with the clock, which nothing re-renders on. It ticks only while the
  // popover is open: nothing on the closed bell shows it.
  const [elapsed, setElapsed] = useState<string | null>(null);
  useEffect(() => {
    if (!opened) return;
    const update = () => setElapsed(formatElapsed(lastResponseAt));
    update();
    const interval = window.setInterval(update, ELAPSED_TICK_MS);
    return () => window.clearInterval(interval);
  }, [opened, lastResponseAt]);

  // Opening an item is the read signal; fire-and-forget, the cache is updated optimistically.
  const handleOpenItem = (notification: EmailResponseNotificationItem) => {
    if (!notification.isRead) markRead(notification.id);
  };

  // Resolves to whether the PDF reached the user, which the pill shows in place.
  const handleDownload = async (notification: EmailResponseNotificationItem, attachmentIndex: number) => {
    const attachment = notification.attachments.find((candidate) => candidate.index === attachmentIndex);
    if (!attachment) return false;
    // Downloading a response is also reading it.
    if (!notification.isRead) markRead(notification.id);
    return downloadAttachment(notification.id, attachment);
  };

  const ariaLabel = hasUnread
    ? `${t('notifications.bell.aria')}, ${unreadCount} ${t('notifications.bell.unreadCount')}`
    : t('notifications.bell.aria');

  return (
    <Popover open={opened} onOpenChange={setOpened}>
      <span className="relative inline-flex">
        <PopoverTrigger asChild>
          <TopbarIconButton label={t('notifications.bell.title')} aria-label={ariaLabel}>
            <BellIcon />
          </TopbarIconButton>
        </PopoverTrigger>
        {hasUnread && (
          <span
            aria-hidden
            className="pointer-events-none absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-err px-1 font-bold text-[10px] text-on-err leading-none ring-2 ring-canvas"
          >
            {capCount(unreadCount, 99)}
          </span>
        )}
      </span>
      {/* z above SystemNotificationBanner (fixed, z 10000), which would otherwise hide the header row. */}
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={8}
        className="z-[10001] flex w-[min(23.75rem,calc(100vw-1rem))] flex-col overscroll-contain p-0"
      >
        <div className="flex flex-col gap-2 px-3 pt-3 pb-2">
          <div className="flex items-center justify-between gap-2">
            {elapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex min-w-0 items-center gap-1.5 text-fg-muted">
                    <ClockIcon className="size-3 shrink-0" aria-hidden />
                    <span className="font-mono font-semibold text-13 text-fg tabular-nums">{elapsed}</span>
                    <span className="truncate text-xs">{t('notifications.bell.sinceLastResponse')}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {`${t('notifications.bell.lastResponseAt')} ${formatWaktuRespon(lastResponseAt?.toISOString() ?? '')}`}
                </TooltipContent>
              </Tooltip>
            ) : (
              <span />
            )}
            <Button
              variant="ghost"
              size="xs"
              onClick={() => markAllRead()}
              disabled={!hasUnread || isMarkingAllRead}
              className="font-medium text-fg-muted"
            >
              {isMarkingAllRead ? <Loader2Icon className="animate-spin" /> : <CheckCheckIcon />}
              {t('notifications.bell.markAllRead')}
            </Button>
          </div>
          <Tabs value={filter} onValueChange={(value) => setFilter(value as 'all' | 'unread')}>
            <TabsList className="h-8">
              <TabsTrigger value="all" className="px-3 text-xs">
                {t('notifications.bell.filterAll')}
              </TabsTrigger>
              <TabsTrigger value="unread" className="px-3 text-xs">
                {hasUnread
                  ? `${t('notifications.bell.filterUnread')} ${unreadCount}`
                  : t('notifications.bell.filterUnread')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {isLoading && (
          <p className="flex items-center justify-center gap-2 py-6 text-fg-muted text-xs">
            <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
            {t('notifications.bell.loading')}
          </p>
        )}

        {!isLoading && isError && (
          <div className="flex flex-col items-center gap-1.5 py-6">
            <p className="text-fg-muted text-xs">{t('notifications.bell.error')}</p>
            <Button variant="link" size="xs" onClick={() => refetch()}>
              <RefreshCwIcon />
              {t('notifications.bell.retry')}
            </Button>
          </div>
        )}

        {!isLoading && !isError && visibleItems.length === 0 && (
          <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
            <p className="font-semibold text-fg text-xs">
              {filter === 'unread' ? t('notifications.bell.emptyUnread') : t('notifications.bell.empty')}
            </p>
            <p className="text-fg-muted text-xs">
              {filter === 'unread' ? t('notifications.bell.emptyUnreadHint') : t('notifications.bell.emptyHint')}
            </p>
          </div>
        )}

        {!isLoading && !isError && visibleItems.length > 0 && (
          // Contained overscroll: the list never hands its leftover scroll to the page behind.
          <ul className="max-h-105 overflow-y-auto overscroll-contain px-1.5 pb-2">
            {visibleItems.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onOpen={handleOpenItem}
                onDownload={handleDownload}
              />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
